import VueSocketIOExt from 'vue-socket.io-extended';
import io, { Socket } from 'socket.io-client';
import { getAccessToken, refreshAccessToken, clearTokens, getRefreshRetryCount } from 'src/helpers/auth';

// Create socket instance with authentication
let socket: Socket | null = null;
let reconnectionAttempts = 0;
const MAX_RECONNECTION_ATTEMPTS = 5;

// Token refresh event emitter for websocket synchronization
export const websocketEvents = new EventTarget();

const createSocket = (): Socket => {
  const token = getAccessToken();
  
  const socketInstance = io(process.env.API as string, {
    transports: ['websocket'],
    auth: {
      token: token
    },
    autoConnect: false // We'll connect manually after setting up listeners
  });

  // Handle authentication errors
  socketInstance.on('connect_error', async (error) => {
    console.log('WebSocket connection error:', error);
    
    // If error is due to authentication, try to refresh token
    if (error.message === 'Authentication error' || error.message === 'jwt expired') {
      console.log('WebSocket authentication failed, attempting token refresh...');
      
      try {
        const newToken = await refreshAccessToken();
        console.log('Token refreshed, reconnecting websocket...');
        
        // Update auth token and reconnect
        socketInstance.auth = { token: newToken };
        socketInstance.connect();
        
        // Emit event to notify other parts of the app about token refresh
        websocketEvents.dispatchEvent(new CustomEvent('token-refreshed', { 
          detail: { token: newToken } 
        }));
        
      } catch (refreshError) {
        console.error('Failed to refresh token for websocket:', refreshError);
        
        // Check if refresh token has reached max retries (handled in auth.ts)
        const retryCount = getRefreshRetryCount();
        console.log(`WebSocket refresh failed, current retry count: ${retryCount}`);
        
        // If refresh token system has reached max retries, it will handle logout
        // We don't need to do additional reconnection attempts here
        if (refreshError.message === 'Max refresh token retries exceeded') {
          console.log('Max refresh token retries exceeded, websocket will not attempt reconnection');
          return;
        }
        
        reconnectionAttempts++;
        if (reconnectionAttempts >= MAX_RECONNECTION_ATTEMPTS) {
          console.log('Max websocket reconnection attempts reached');
          // Don't clear tokens here - let the refresh token system handle it
        }
      }
    }
  });

  // Reset reconnection attempts on successful connection
  socketInstance.on('connect', () => {
    console.log('WebSocket connected successfully');
    reconnectionAttempts = 0;
  });

  // Handle disconnection
  socketInstance.on('disconnect', (reason) => {
    console.log('WebSocket disconnected:', reason);
    
    // If disconnected due to server-side issues, attempt reconnection
    if (reason === 'io server disconnect' || reason === 'io client disconnect') {
      console.log('Attempting to reconnect websocket...');
      setTimeout(() => {
        if (socket && !socket.connected) {
          const token = getAccessToken();
          if (token) {
            socket.auth = { token };
            socket.connect();
          }
        }
      }, 1000);
    }
  });

  return socketInstance;
};

// Function to reconnect websocket with new token
export const reconnectWebSocketWithNewToken = (newToken: string) => {
  if (socket) {
    console.log('Reconnecting websocket with new token...');
    socket.auth = { token: newToken };
    
    if (socket.connected) {
      socket.disconnect();
    }
    socket.connect();
  }
};

// Function to get or create socket instance
export const getSocket = (): Socket => {
  if (!socket) {
    socket = createSocket();
  }
  return socket;
};

// Enhanced setup function
export const setupWebSocket = (app: any) => {
  socket = createSocket();
  
  // Connect the socket
  if (getAccessToken()) {
    socket.connect();
  }
  
  app.use(VueSocketIOExt, socket);
  
  // Listen for token refresh events from HTTP interceptors
  websocketEvents.addEventListener('http-token-refreshed', ((event: CustomEvent) => {
    const newToken = event.detail.token;
    reconnectWebSocketWithNewToken(newToken);
  }) as EventListener);
};

// Export the socket for direct access if needed
export { socket };
